import { Injectable, inject } from '@angular/core';
import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  Unsubscribe,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { AssetLineage, CreateAsset, Project } from '../models/project.model';
import { groupIntoLineages } from './asset-lineage.util';
import { FirebaseService } from './firebase.service';
import { OrgContextService } from './org-context.service';

/**
 * CRUD for Create projects and their generated assets.
 *
 * Projects are **per user**, persisted in Firestore at:
 *   users/{uid}/create-projects/{projectId}
 *   users/{uid}/create-projects/{projectId}/assets/{assetId}
 *
 * Access is gated by the `create-projects` rules in crewdle-sdk-firebase
 * (owner-based: the signed-in user can only touch their own subtree).
 * Generated binaries live in Firebase Storage under
 * `users/{uid}/create/{projectId}/...` — see GenerationService.
 */
/** Exact payload accepted by the `revertAssetVersion` callable. */
export interface RevertAssetVersionRequest {
  organizationId: string;
  projectId: string;
  lineageId: string;
  targetVersionId: string;
}

/** The storyboard slot an imported version belongs to. */
export type StoryboardAssetTarget = 'frame-image' | 'frame-video' | 'narration' | 'music';

/**
 * Where the imported binary comes from: a file the client already uploaded into
 * its own project prefix, or an existing asset picked from the user's library.
 * The callable always copies the binary, so neither source is left referenced.
 */
export type StoryboardAssetSource =
  | { kind: 'upload'; storagePath: string; contentType: string }
  /**
   * `sourceProjectId` is the container the picked asset is stored in. The asset
   * picker lists every container the user owns and an asset id is only unique
   * within its own, so omitting it makes a pick from a folder unresolvable.
   */
  | { kind: 'library'; sourceAssetId: string; sourceProjectId?: string };

/** Exact payload accepted by the `appendStoryboardAssetVersion` callable. */
export interface AppendStoryboardAssetVersionRequest {
  organizationId: string;
  projectId: string;
  /** Absent for the slot's very first version — the callable mints the lineage. */
  lineageId?: string;
  target: StoryboardAssetTarget;
  /** The storyboard the slot belongs to; the callable writes its lineage id there. */
  storyboardId: string;
  /** Required by the two scene targets; the audio lanes live on the timeline. */
  frameId?: string;
  source: StoryboardAssetSource;
}

/**
 * The appended version, field for field as the callable returns it. The index
 * signature is deliberate: the callable may grow fields, and a response the
 * client does not read yet must never break the build.
 */
export interface AppendStoryboardAssetVersionResponse {
  lineageId: string;
  /** id of the asset document written for the new version — what a pointer names. */
  assetId: string;
  versionNumber: number;
  type: 'image' | 'video' | 'audio';
  url: string;
  storagePath: string;
  contentType: string;
  [key: string]: unknown;
}

/** Exact payload accepted by the `exportStoryboardAssetToLibrary` callable. */
export interface ExportStoryboardAssetToLibraryRequest {
  organizationId: string;
  projectId: string;
  sourceAssetId: string;
  /** Destination folder from the picker; absent means the root container. */
  folderId?: string;
  title?: string;
}

/** The user-visible lineage the export minted. Permissive, as above. */
export interface ExportStoryboardAssetToLibraryResponse {
  lineageId?: string;
  assetId?: string;
  url?: string;
  [key: string]: unknown;
}

export interface ReconcileProjectLineageCountRequest {
  organizationId: string;
  projectId: string;
}

export interface ReconcileProjectLineageCountResponse {
  projectId: string;
  count: number;
  changed: boolean;
}

/** Contract for the backend-owned, retryable folder cleanup state machine. */
export interface DeleteCreateFolderRequest {
  organizationId: string;
  projectId: string;
}

export interface DeleteCreateFolderResponse {
  removed: boolean;
  existing: boolean;
}

/** Builds the revert payload; exported so the contract is unit-testable. */
export function revertAssetVersionPayload(
  organizationId: string,
  projectId: string,
  lineageId: string,
  targetVersionId: string,
): RevertAssetVersionRequest {
  return { organizationId, projectId, lineageId, targetVersionId };
}

/**
 * Whether an owner-scoped container belongs to the active organization.
 *
 * Create projects lived exclusively under the user's Firestore subtree before
 * organization stamping was introduced. Those unstamped documents are still
 * owner containers and are adopted lazily by backend callables; rejecting them
 * here would orphan their generated media in the UI. A present stamp remains
 * authoritative, so a container stamped to another organization never crosses
 * org boundaries.
 */
export function isCreateContainerInOrganization(
  project: Project,
  organizationId: string | undefined,
): boolean {
  return !!organizationId && (!project.organizationId || project.organizationId === organizationId);
}

/** A persisted owner container is reusable inside its org, including legacy unstamped ones. */
export function isUsableCreateContainer(
  project: Project | null,
  organizationId: string | undefined,
): project is Project {
  return !!project && isCreateContainerInOrganization(project, organizationId) && !project.isHidden;
}

/** `null` means remove a redundant display overlay. */
export function lineageFolderOverlay(projectId: string, targetFolderId: string): string | null {
  return targetFolderId === projectId ? null : targetFolderId;
}

@Injectable({ providedIn: 'root' })
export class ProjectService {
  private readonly firebase = inject(FirebaseService);
  private readonly orgContext = inject(OrgContextService);
  /** One callable at most per legacy project for this service session. */
  private readonly lineageReconciliations = new Map<string, Promise<number>>();
  /** One root-container resolution at most per org for this service session. */
  private readonly rootContainers = new Map<string, Promise<string>>();

  private get uid(): string {
    const uid = this.firebase.auth.currentUser?.uid;
    if (!uid) throw new Error('User not logged in.');
    return uid;
  }

  private projectsRef() {
    return collection(this.firebase.firestore, 'users', this.uid, 'create-projects');
  }

  private projectRef(projectId: string) {
    return doc(this.firebase.firestore, 'users', this.uid, 'create-projects', projectId);
  }

  private assetsRef(projectId: string) {
    return collection(
      this.firebase.firestore,
      'users',
      this.uid,
      'create-projects',
      projectId,
      'assets',
    );
  }

  /**
   * Lists the signed-in user's folders, scoped to the active organization.
   *
   * Folders live under the user's subtree but carry an `organizationId` stamped
   * at creation, so the same account sees a distinct set per org. Filtering is
   * done client-side to avoid a composite index (`organizationId` + `updatedAt`);
   * a user's folder count is small. Legacy folders created before org stamping
   * (no `organizationId`) surface in every org so they aren't orphaned.
   *
   * The root container and hidden tombstones are storage locations, not folders,
   * so they are excluded — use `listAssetContainers()` when the caller needs to
   * reach every asset rather than every folder.
   */
  async listProjects(): Promise<Project[]> {
    const containers = await this.listAssetContainers();
    return containers.filter((p) => !p.isRoot && !p.isHidden);
  }

  /**
   * Every document that can physically hold assets: folders, the root container
   * and hidden tombstones. Asset fan-outs (the library pickers, the Home view)
   * must use this — going through `listProjects()` would silently skip unfiled
   * assets and assets stranded under a tombstoned folder.
   */
  async listAssetContainers(): Promise<Project[]> {
    const orgId = this.orgContext.currentOrg()?.id;
    const snap = await getDocs(query(this.projectsRef(), orderBy('updatedAt', 'desc')));
    return snap.docs
      .map((d) => this.toProject(d.id, d.data()))
      .filter((p) => isCreateContainerInOrganization(p, orgId));
  }

  /**
   * Deterministic id for the active org's root container, so two concurrent
   * callers converge on one document instead of racing into duplicates.
   */
  rootContainerId(): string {
    const orgId = this.orgContext.currentOrg()?.id;
    if (!orgId) throw new Error('No organization selected.');
    return `root-${orgId}`;
  }

  /**
   * Whether an id is the active org's root container. Callers that only need to
   * branch on it (e.g. building a back link) shouldn't have to handle the
   * no-organization throw, so this returns false instead.
   */
  isRootContainer(id: string): boolean {
    const orgId = this.orgContext.currentOrg()?.id;
    return !!orgId && id === `root-${orgId}`;
  }

  /**
   * Returns the id of the container that stores unfiled assets, creating it on
   * first use. Cached per service session, so this costs one read per session.
   */
  ensureRootContainer(): Promise<string> {
    const id = this.rootContainerId();
    const existing = this.rootContainers.get(id);
    if (existing) return existing;
    const request = this.createRootContainer(id).catch((error) => {
      // A failed attempt must not poison the cache for the rest of the session.
      this.rootContainers.delete(id);
      throw error;
    });
    this.rootContainers.set(id, request);
    return request;
  }

  private async createRootContainer(id: string): Promise<string> {
    const uid = this.uid;
    const snap = await getDoc(this.projectRef(id));
    if (snap.exists()) return id;
    // `lineageCount` is deliberately omitted: firestore.rules rejects a
    // create that carries it, and the server owns the counter afterwards.
    // `merge` keeps a concurrent creator from erroring.
    await setDoc(
      this.projectRef(id),
      {
        name: '',
        description: '',
        ownerId: uid,
        organizationId: this.orgContext.currentOrg()?.id,
        isRoot: true,
        assetCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
    return id;
  }

  async getProject(projectId: string): Promise<Project | null> {
    const snap = await getDoc(this.projectRef(projectId));
    return snap.exists() ? this.toProject(snap.id, snap.data()) : null;
  }

  async createProject(name: string, description = ''): Promise<Project> {
    const uid = this.uid;
    const ref = doc(this.projectsRef());
    // Stamp the active org so backend triggers (e.g. the "first Create
    // generation" credit milestone) can resolve which org earned it.
    const organizationId = this.orgContext.currentOrg()?.id;
    // `lineageCount` is deliberately omitted, as in `createRootContainer`:
    // firestore.rules rejects a create that carries it, and the server owns the
    // counter afterwards. The returned model still reports 0 so the caller's
    // session doesn't mistake a brand-new folder for a legacy one to reconcile.
    const data: Record<string, unknown> = {
      name,
      description,
      ownerId: uid,
      assetCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    if (organizationId) data['organizationId'] = organizationId;
    await setDoc(ref, data);
    const now = Date.now();
    return {
      id: ref.id,
      ownerId: uid,
      name,
      description,
      organizationId,
      assetCount: 0,
      lineageCount: 0,
      createdAt: now,
      updatedAt: now,
    };
  }

  async renameProject(projectId: string, name: string, description?: string): Promise<void> {
    const patch: Record<string, unknown> = { name, updatedAt: serverTimestamp() };
    if (description !== undefined) patch['description'] = description;
    await updateDoc(this.projectRef(projectId), patch);
  }

  async listAssets(projectId: string): Promise<CreateAsset[]> {
    const snap = await getDocs(query(this.assetsRef(projectId), orderBy('createdAt', 'desc')));
    return snap.docs.flatMap((d) => {
      const asset = toCreateAsset(d.id, d.data(), projectId);
      return asset ? [asset] : [];
    });
  }

  /** Lists one asset per version lineage — the current head of each. */
  async listHeadAssets(projectId: string): Promise<CreateAsset[]> {
    const all = await this.listAssets(projectId);
    return groupIntoLineages(all).map((lineage) =>
      lineage.generatedTitle && !lineage.head.title
        ? { ...lineage.head, title: lineage.generatedTitle }
        : lineage.head,
    );
  }

  /**
   * Loads every asset the user owns in the active org, across all containers.
   *
   * Home needs this because `folderId` decouples where an asset displays from
   * where it is stored, so neither the root view nor a folder view can be
   * answered by one container's subcollection. Costs `1 + N` queries for N
   * containers; a user's container count is small and `listAssetContainers()`
   * is already loaded for the folder tiles. If per-user asset volume ever grows
   * past the low hundreds, replace this with a `createdBy`-filtered collection
   * group query (which needs a rules block and a composite index).
   */
  async listAllAssets(containerIds: string[]): Promise<CreateAsset[]> {
    const perContainer = await Promise.all(
      containerIds.map((id) => this.listAssets(id).catch(() => [])),
    );
    return perContainer.flat();
  }

  /** Keeps visible lineage cards current when a nonblocking title job lands. */
  subscribeHeadAssets(projectId: string, onChange: (assets: CreateAsset[]) => void): Unsubscribe {
    return onSnapshot(
      query(this.assetsRef(projectId), orderBy('createdAt', 'desc')),
      (snapshot) => {
        const assets = snapshot.docs.flatMap((item) => {
          const asset = toCreateAsset(item.id, item.data(), projectId);
          return asset ? [asset] : [];
        });
        onChange(
          groupIntoLineages(assets).map((lineage) =>
            lineage.generatedTitle && !lineage.head.title
              ? { ...lineage.head, title: lineage.generatedTitle }
              : lineage.head,
          ),
        );
      },
    );
  }

  /** Fetches a single lineage (all versions + the current head) by id. */
  async getLineage(projectId: string, lineageId: string): Promise<AssetLineage | undefined> {
    const all = await this.listAssets(projectId);
    return groupIntoLineages(all).find((l) => l.lineageId === lineageId);
  }

  /**
   * Live view of one lineage. The workspace needs this because the backend writes
   * fields — most visibly the generated title — seconds *after* the asset itself
   * exists, so a one-shot read taken on open never sees them and the header stays
   * "Untitled" for the rest of the session.
   *
   * Emits `undefined` if the lineage disappears (deleted elsewhere), which callers
   * must not confuse with "not loaded yet".
   */
  subscribeLineage(
    projectId: string,
    lineageId: string,
    onChange: (lineage: AssetLineage | undefined) => void,
  ): Unsubscribe {
    return onSnapshot(
      query(this.assetsRef(projectId), orderBy('createdAt', 'desc')),
      (snapshot) => {
        const assets = snapshot.docs.flatMap((item) => {
          const asset = toCreateAsset(item.id, item.data(), projectId);
          return asset ? [asset] : [];
        });
        onChange(groupIntoLineages(assets).find((lineage) => lineage.lineageId === lineageId));
      },
    );
  }

  /**
   * Live view of every lineage in a container, grouped once.
   *
   * The storyboard workspace needs many lineages at once (a still and a clip per
   * scene, plus narration and music). Storyboard media lives in the root
   * container alongside the user's Home assets, so one listener over the
   * collection answers every rail — attaching one per rail would open the same
   * query twenty times for a ten-scene storyboard.
   */
  subscribeLineages(projectId: string, onChange: (lineages: AssetLineage[]) => void): Unsubscribe {
    return onSnapshot(
      query(this.assetsRef(projectId), orderBy('createdAt', 'desc')),
      (snapshot) => {
        const assets = snapshot.docs.flatMap((item) => {
          const asset = toCreateAsset(item.id, item.data(), projectId);
          return asset ? [asset] : [];
        });
        onChange(groupIntoLineages(assets));
      },
    );
  }

  /**
   * Reverts through the server so the head switch, canonical snapshot and any
   * pending proposal invalidation happen in one transaction.
   *
   * The callable requires `organizationId` + `targetVersionId`; the previous
   * `{ projectId, lineageId, versionId }` shape was silently rejected (A1).
   */
  async revertToVersion(
    projectId: string,
    lineageId: string,
    targetVersionId: string,
  ): Promise<{ headVersionId: string }> {
    const organizationId = this.orgContext.currentOrg()?.id;
    if (!organizationId) throw new Error('No organization selected.');
    const fn = httpsCallable<RevertAssetVersionRequest, { headVersionId: string }>(
      this.firebase.functions,
      'revertAssetVersion',
    );
    const result = await fn(
      revertAssetVersionPayload(organizationId, projectId, lineageId, targetVersionId),
    );
    return { headVersionId: result.data?.headVersionId ?? targetVersionId };
  }

  /**
   * Appends a version to a storyboard slot's lineage — a still picked from the
   * library, or a hand-composed still the client has already uploaded.
   *
   * The server copies the binary into the storyboard's own project prefix and
   * marks the lineage internal, so the imported version never appears on Home and
   * the picked source survives the storyboard being deleted. It also records the
   * slot's lineage id on the frame or timeline — hence the storyboard coordinates,
   * which are what let the rail survive a reload and the delete cascade find a
   * lineage that never had a generation job.
   */
  async appendStoryboardAssetVersion(options: {
    projectId: string;
    storyboardId: string;
    target: StoryboardAssetTarget;
    source: StoryboardAssetSource;
    /** The scene's frame; omitted for the narration and music lanes. */
    frameId?: string;
    lineageId?: string;
  }): Promise<AppendStoryboardAssetVersionResponse> {
    const organizationId = this.orgContext.currentOrg()?.id;
    if (!organizationId) throw new Error('No organization selected.');
    const fn = httpsCallable<
      AppendStoryboardAssetVersionRequest,
      AppendStoryboardAssetVersionResponse
    >(this.firebase.functions, 'appendStoryboardAssetVersion');
    // `lineageId` and `frameId` are omitted rather than sent as undefined: the
    // slot's first version has no lineage yet, an audio lane has no frame, and the
    // callable validates the payload shape.
    const request: AppendStoryboardAssetVersionRequest = {
      organizationId,
      projectId: options.projectId,
      storyboardId: options.storyboardId,
      target: options.target,
      source: options.source,
    };
    if (options.frameId) request.frameId = options.frameId;
    if (options.lineageId) request.lineageId = options.lineageId;
    const result = await fn(request);
    const data = result.data;
    if (!data?.lineageId || !data?.assetId || !data?.url) {
      throw new Error('Invalid storyboard asset version response.');
    }
    return data;
  }

  /**
   * Copies the storyboard asset `sourceAssetId` out to the user's library as a
   * new, user-visible lineage in `folderId` (absent = the root container).
   *
   * A fresh lineage with no job and no frame reference, so deleting the
   * storyboard can never sweep the exported copy.
   */
  async exportStoryboardAssetToLibrary(
    projectId: string,
    sourceAssetId: string,
    options: { folderId?: string; title?: string } = {},
  ): Promise<ExportStoryboardAssetToLibraryResponse> {
    const organizationId = this.orgContext.currentOrg()?.id;
    if (!organizationId) throw new Error('No organization selected.');
    const fn = httpsCallable<
      ExportStoryboardAssetToLibraryRequest,
      ExportStoryboardAssetToLibraryResponse
    >(this.firebase.functions, 'exportStoryboardAssetToLibrary');
    const request: ExportStoryboardAssetToLibraryRequest = {
      organizationId,
      projectId,
      sourceAssetId,
    };
    if (options.folderId) request.folderId = options.folderId;
    const title = options.title?.trim();
    if (title) request.title = title;
    const result = await fn(request);
    const data = result.data;
    if (!data) throw new Error('Invalid library export response.');
    return data;
  }

  /**
   * Lazily repairs a legacy project's denormalized HEAD count. The callable is
   * intentionally isolated here so backend naming and payload stay centralized.
   */
  reconcileProjectLineageCount(projectId: string): Promise<number> {
    const organizationId = this.orgContext.currentOrg()?.id;
    if (!organizationId) return Promise.reject(new Error('No organization selected.'));
    const key = `${organizationId}:${projectId}`;
    const existing = this.lineageReconciliations.get(key);
    if (existing) return existing;

    const request = (async () => {
      const result = await this.invokeLineageReconciliation({ organizationId, projectId });
      const count = result.data?.count;
      if (!Number.isFinite(count) || count < 0) {
        throw new Error('Invalid lineage count response.');
      }
      return Math.floor(count);
    })();
    this.lineageReconciliations.set(key, request);
    // Only settled successes stay memoized: the backend's `aborted` (and any
    // transient failure) is explicitly retryable, so a cached rejection would
    // poison every later caller for the rest of the session.
    request.catch(() => {
      if (this.lineageReconciliations.get(key) === request) {
        this.lineageReconciliations.delete(key);
      }
    });
    return request;
  }

  private invokeLineageReconciliation(request: ReconcileProjectLineageCountRequest) {
    const fn = httpsCallable<
      ReconcileProjectLineageCountRequest,
      ReconcileProjectLineageCountResponse
    >(this.firebase.functions, 'reconcileProjectLineageCount');
    return fn(request);
  }

  /** Returns an updated local model after a once-per-session legacy repair. */
  async reconcileLegacyProject(project: Project): Promise<Project> {
    if (project.lineageCount !== undefined) return project;
    const lineageCount = await this.reconcileProjectLineageCount(project.id);
    return { ...project, lineageCount };
  }

  /** Persists a generated asset and bumps the parent project's counters. */
  async addAsset(
    projectId: string,
    asset: Omit<CreateAsset, 'id' | 'createdAt' | 'createdBy'>,
  ): Promise<CreateAsset> {
    const uid = this.uid;
    const ref = doc(this.assetsRef(projectId));
    const createdAt = Date.now();
    const data: Record<string, unknown> = {
      ...asset,
      createdBy: uid,
      createdAt: serverTimestamp(),
    };
    // Firestore rejects `undefined` field values (e.g. revisedPrompt on non-image assets).
    Object.keys(data).forEach((key) => data[key] === undefined && delete data[key]);
    await setDoc(ref, data);
    // A5: the server owns both counters. `writeAssetVersion` increments
    // `lineageCount` and mirrors `assetCount`, and the `countDirectAssetLineage`
    // trigger covers writes that bypass it — so incrementing here counted the
    // same asset twice, permanently, with nothing to reconcile it back.
    await updateDoc(this.projectRef(projectId), {
      coverUrl: asset.url,
      coverType: asset.type,
      updatedAt: serverTimestamp(),
    }).catch(() => undefined);
    return { id: ref.id, ...asset, createdBy: uid, createdAt };
  }

  /** Deletes lineage metadata and binaries through the idempotent backend operation. */
  async deleteLineage(lineage: AssetLineage): Promise<void> {
    const projectId = lineage.head.projectId;
    if (!projectId) throw new Error('Asset is not associated with a folder.');
    const organizationId = this.orgContext.currentOrg()?.id;
    if (!organizationId) throw new Error('No organization selected.');
    await this.invokeLineageDeletion({
      organizationId,
      projectId,
      lineageId: lineage.lineageId,
    });
  }

  private invokeLineageDeletion(request: {
    organizationId: string;
    projectId: string;
    lineageId: string;
  }) {
    const fn = httpsCallable<typeof request, { removed: boolean; existing: boolean }>(
      this.firebase.functions,
      'deleteAssetLineage',
    );
    return fn(request);
  }

  /**
   * Files a lineage into `targetFolderId` by overlaying `folderId` on every one
   * of its versions. Nothing is relocated — see `CreateAsset.folderId` for why a
   * physical move would destroy seed frames and chat history.
   *
   * Moving a lineage back to the folder it is physically stored in clears the
   * overlay rather than writing a redundant one.
   */
  async moveLineage(lineage: AssetLineage, targetFolderId: string): Promise<void> {
    const projectId = lineage.head.projectId;
    if (!projectId) throw new Error('Asset is not associated with a folder.');
    const batch = writeBatch(this.firebase.firestore);
    const overlay = lineageFolderOverlay(projectId, targetFolderId);
    const folderId = overlay === null ? deleteField() : overlay;
    for (const version of lineage.versions) {
      batch.update(doc(this.assetsRef(projectId), version.id), { folderId });
    }
    batch.update(this.projectRef(projectId), { updatedAt: serverTimestamp() });
    if (targetFolderId !== projectId) {
      batch.update(this.projectRef(targetFolderId), { updatedAt: serverTimestamp() });
    }
    await batch.commit();
  }

  /**
   * Renames a lineage by writing `title` onto every one of its versions, the same
   * way `moveLineage` overlays `folderId`.
   *
   * Every version carries the title because a title may legitimately live on any
   * version (`generatedLineageTitle`) and because reverting switches which version
   * is the head — writing only the head would silently lose the name on the next
   * revert.
   *
   * The name must not be empty. A generated title is stored in this same field and
   * title generation is requested once per session, so clearing it would destroy
   * the name with nothing to fall back to.
   */
  async renameLineage(lineage: AssetLineage, title: string): Promise<void> {
    const projectId = lineage.head.projectId;
    if (!projectId) throw new Error('Asset is not associated with a folder.');
    const trimmed = title.trim();
    if (!trimmed) throw new Error('An asset name cannot be empty.');
    const batch = writeBatch(this.firebase.firestore);
    for (const version of lineage.versions) {
      batch.update(doc(this.assetsRef(projectId), version.id), { title: trimmed });
    }
    batch.update(this.projectRef(projectId), { updatedAt: serverTimestamp() });
    await batch.commit();
  }

  /**
   * Deletes a folder through the backend state machine. The server marks the
   * folder deleting before rejecting new writes, migrates legacy storyboard and
   * walkthrough ownership to root, and only finalizes after rechecking all
   * children. Keeping this operation server-side removes the client-side
   * check/delete race and makes retries safe after an interrupted cleanup.
   */
  async deleteFolder(folderId: string, members: AssetLineage[]): Promise<'deleted'> {
    const organizationId = this.orgContext.currentOrg()?.id;
    if (!organizationId) throw new Error('No organization selected.');
    // Each lineage operation is independently idempotent. Stop on the first
    // failure and leave the folder visible so retry resumes safely.
    for (const lineage of members) await this.deleteLineage(lineage);
    const result = await this.invokeFolderDeletion({ organizationId, projectId: folderId });
    if (result.data?.removed !== true) {
      throw new Error('Invalid folder deletion response.');
    }
    return 'deleted';
  }

  private invokeFolderDeletion(request: DeleteCreateFolderRequest) {
    const fn = httpsCallable<DeleteCreateFolderRequest, DeleteCreateFolderResponse>(
      this.firebase.functions,
      'deleteCreateProject',
    );
    return fn(request);
  }

  /** Maps a Firestore document (with server Timestamps) to a Project. */
  private toProject(id: string, data: any): Project {
    const toMillis = (v: any): number =>
      v?.toMillis ? v.toMillis() : typeof v === 'number' ? v : Date.now();
    return {
      id,
      ownerId: data['ownerId'] ?? '',
      organizationId: data['organizationId'],
      name: data['name'] ?? 'Untitled',
      description: data['description'] ?? '',
      coverUrl: data['coverUrl'],
      coverType: data['coverType'],
      assetCount: data['assetCount'] ?? 0,
      lineageCount:
        typeof data['lineageCount'] === 'number' && Number.isFinite(data['lineageCount'])
          ? Math.max(0, Math.floor(data['lineageCount']))
          : undefined,
      isRoot: data['isRoot'] === true,
      isHidden: data['isHidden'] === true,
      createdAt: toMillis(data['createdAt']),
      updatedAt: toMillis(data['updatedAt']),
    };
  }
}

/**
 * Epoch millis from whatever Firestore handed back.
 *
 * `CreateAsset.createdAt` is typed `number`, but the SDK returns a `Timestamp`
 * object. Arithmetic on one does not throw — `Timestamp.valueOf()` returns a
 * zero-padded seconds-since-year-1 string for lexicographic ordering, so
 * `Math.max` coerced it to a plausible-looking ~6.4e10 and dates rendered as
 * "Jan 10, 1972" while sorting put every asset below every storyboard. Converting
 * here makes the declared type honest for every consumer.
 */
function assetMillis(value: unknown): number | undefined {
  if (typeof value === 'number') return value;
  const timestamp = value as { toMillis?: () => number; seconds?: number } | undefined;
  if (typeof timestamp?.toMillis === 'function') return timestamp.toMillis();
  if (typeof timestamp?.seconds === 'number') return timestamp.seconds * 1000;
  return undefined;
}

/** Runtime boundary for Firestore asset documents, including legacy subtype fallback. */
export function toCreateAsset(
  id: string,
  data: Record<string, unknown>,
  projectId?: string,
): CreateAsset | null {
  const rawType = data['type'];
  const subtype = data['generationSubtype'] ?? data['generationKind'];
  const type =
    rawType === 'image' || rawType === 'video' || rawType === 'audio'
      ? rawType
      : subtype === 'image'
        ? 'image'
        : subtype === 'video'
          ? 'video'
          : subtype === 'speech' || subtype === 'music'
            ? 'audio'
            : null;
  if (!type) return null;
  const createdAt = assetMillis(data['createdAt']);
  const updatedAt = assetMillis(data['updatedAt']);
  return {
    id,
    ...(data as Omit<CreateAsset, 'id' | 'type'>),
    type,
    // Resolved here rather than left to each consumer: the backend writes
    // `generationKind`, so anything reading only `generationSubtype` saw every
    // narration and every music track as an undifferentiated "Audio".
    ...(subtype === 'image' || subtype === 'video' || subtype === 'speech' || subtype === 'music'
      ? { generationSubtype: subtype }
      : {}),
    ...(createdAt === undefined ? {} : { createdAt }),
    ...(updatedAt === undefined ? {} : { updatedAt }),
    projectId,
  };
}
